import { createContainer } from 'containers';
import { PhotoGalleryQuery, PhotoGalleryQueryVariables } from 'queries';
import { useEffect, useState } from 'react';
import PhotoGallerySlideshowModal from './components/PhotoGallerySlideshowModal';
import { ExtractTypename } from '@sblive-sports/api-client-js';
import { localCache } from 'helpers/cache';
import PhotoGalleryAlertModal, { PhotoGalleryAlertTypeEnum } from './components/PhotoGalleryAlertModal';
import { useApplicationContainer } from 'containers/ApplicationContainer';
import PhotoGalleryCart from './components/PhotoGalleryCart';

const SBL_PHOTOS_PREFIX = 'photo_gallery';
export const SBL_PHOTOGRAPHER_COPYRIGHT_KEY = `${SBL_PHOTOS_PREFIX}_copyright_agreements_accepted`;
export const SBL_PHOTO_PURCHASE_IN_PROGRESS = `${SBL_PHOTOS_PREFIX}_purchase_in_progress`;
export const SBL_PHOTO_CART_ITEMS = `${SBL_PHOTOS_PREFIX}_selected_photos`;

type CopyrightAgreementsAcceptedType = Array<{
  [key: string]: number;
}>;

type CurrentSelectedPhotoType = {
  resolution: string;
  displayPrice: string;
  price: number;
  isHighResolution: boolean;
};
export type SelectedPhotoType = CurrentSelectedPhotoType & { index: number };

export type Photo = ExtractTypename<PhotoGalleryQuery['contentItem'], 'ContentItemPhotoGallery'>['photos'][number];
type Ad = { ad: boolean };
type PhotoOrAd = Photo | Ad;

const isPhoto = (p: any): p is Photo => !!(p.s3Watermark || p.watermark) && !!p.filename;
const isAd = (a: any): a is Ad => !!a.ad;

export interface PhotoGalleryProps {
  query: Omit<PhotoGalleryQuery, 'contentItem'> & {
    contentItem: ExtractTypename<PhotoGalleryQuery['contentItem'], 'ContentItemPhotoGallery'>;
  };
  variables: PhotoGalleryQueryVariables;
  path: string;
}

export const [PhotoGalleryContainer, usePhotoGalleryContainer] = createContainer(({ query }: PhotoGalleryProps) => {
  const photoGallery = query.contentItem;
  const [{ pathname }, { handleShare, getCurrentQueryStringParam, makePath, replacePath }] = useApplicationContainer();
  const photoIndexQueryString = getCurrentQueryStringParam('photoIndex');
  const [slideshowIsOpen, setSlideshowIsOpen] = useState(Boolean(photoIndexQueryString));
  const [currentIndex, setCurrentIndex] = useState(parseInt(photoIndexQueryString || '0', 10) - 1);
  const [showCart, setShowCart] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<SelectedPhotoType[]>([]);
  const [currentSelection, setCurrentSelection] = useState<CurrentSelectedPhotoType>({
    resolution: '3000 x 2216 px (300 dpi)',
    displayPrice: photoGallery.highResolutionDisplayPrice,
    price: photoGallery.highResolutionPriceInCents,
    isHighResolution: true
  });
  const [alertType, setAlertType] = useState<PhotoGalleryAlertTypeEnum | null>(null);

  const slideAdIndex = 4;
  const photoSlides: PhotoOrAd[] = [
    ...photoGallery.photos.slice(0, slideAdIndex),
    { ad: true },
    ...photoGallery.photos.slice(slideAdIndex, photoGallery.photos.length)
  ];

  const { start, end } = getThumbnailRange(currentIndex, photoSlides.length, 9);
  const thumbnailsToShow = photoSlides.slice(start, end);

  function handleShareGallery() {
    handleShare({ title: photoGallery.name, path: pathname });
  }

  function handleSwitchClick() {
    handleChangeIndex(0);
    setSlideshowIsOpen(true);
  }

  function handleThumbnailClick(index: number) {
    handleChangeIndex(index >= slideAdIndex ? index + 1 : index);
    setSlideshowIsOpen(true);
  }

  function getNextImage() {
    handleChangeIndex((currentIndex + 1) % photoSlides.length);
  }

  function getPreviousImage() {
    handleChangeIndex((currentIndex - 1 + photoGallery.photos.length) % photoGallery.photos.length);
  }

  function getThumbnailRange(currentIndex: number, totalImages: number, maxThumbnails: number) {
    const halfMax = Math.floor(maxThumbnails / 2);
    let start = Math.max(currentIndex - halfMax, 0);
    let end = Math.min(start + maxThumbnails, totalImages);

    if (end === totalImages) {
      start = Math.max(totalImages - maxThumbnails, 0);
    }

    return { start, end };
  }

  function acceptCopyrightAgreement(url: string) {
    const existingAgreements = localCache.get<CopyrightAgreementsAcceptedType>(SBL_PHOTOGRAPHER_COPYRIGHT_KEY) ?? {};
    localCache.set(SBL_PHOTOGRAPHER_COPYRIGHT_KEY, {
      ...existingAgreements,
      [url]: new Date().getTime()
    });
  }

  useEffect(() => {
    const purchaseInProgress = localCache.get(SBL_PHOTO_PURCHASE_IN_PROGRESS);
    const purchaseStatus = getCurrentQueryStringParam('purchase');

    const redirectFromStripeSuccess = purchaseStatus === 'successful';
    if (purchaseInProgress && redirectFromStripeSuccess) {
      setAlertType(PhotoGalleryAlertTypeEnum.PURCHASE_CONFIRMATION);
      localCache.delete(SBL_PHOTO_CART_ITEMS);
      window.history.pushState({}, document.title, window.location.pathname);
      return;
    }

    const redirectFromStripeCancel = purchaseStatus === 'cancel';
    if (purchaseInProgress && redirectFromStripeCancel) {
      // repopulate cart
      const previouslySelectedPhotos = localCache.get<SelectedPhotoType[]>(SBL_PHOTO_CART_ITEMS) ?? [];
      setSelectedPhotos(previouslySelectedPhotos);
      window.history.pushState({}, document.title, window.location.pathname);
      return;
    }

    const existingAcceptedAgreements = localCache.get<CopyrightAgreementsAcceptedType>(SBL_PHOTOGRAPHER_COPYRIGHT_KEY) ?? {};
    const agreementAccepted = Object.keys(existingAcceptedAgreements).includes(window.location.origin);
    if (!agreementAccepted) {
      setAlertType(PhotoGalleryAlertTypeEnum.COPYRIGHT_NOTICE);
      return;
    }
  }, []);

  function handleChangeIndex(index: number) {
    if (index === currentIndex) return;
    setCurrentIndex(index);
    const pathname = makePath(
      { path: photoGallery.webPath.slice(1) },
      {
        photoIndex: index + 1,
        back: getCurrentQueryStringParam('back')
      }
    );
    handleChangeUrl(pathname);
  }

  function handleChangeUrl(pathname: string) {
    replacePath(pathname);

    window.dataLayer?.push({
      event: 'page_view',
      page_path: pathname,
      page_title: document.title
    });
  }

  return [
    {
      slideshowIsOpen,
      photoGallery,
      currentIndex,
      thumbnailsToShow,
      start,
      showCart,
      selectedPhotos,
      currentSelection,
      alertType,
      photoSlides,
      slideAdIndex
    },
    {
      handleSwitchClick,
      handleThumbnailClick,
      getNextImage,
      getPreviousImage,
      setShowCart,
      setSelectedPhotos,
      setCurrentSelection,
      setAlertType,
      acceptCopyrightAgreement,
      handleShareGallery,
      handleChangeIndex,
      isPhoto,
      isAd
    },
    children => {
      return (
        <>
          {slideshowIsOpen && <PhotoGallerySlideshowModal />}
          {alertType && <PhotoGalleryAlertModal />}
          <PhotoGalleryCart />
          {!slideshowIsOpen && children}
        </>
      );
    }
  ];
});
